import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
/**
 * Pipelines and stages (RELAY-948). The model agreed on 2026-09-24 is
 * Project → Deals (payments) → Jobs → Shortlists → Candidates, and each level moves through a
 * stage list the organisation's admins edit:
 *
 *   project   — Sales Flow     (Lead → Discovery → Proposal → Active → Won / Lost)
 *   deal      — Deal Flow      (Proposal → Agreed → Invoiced → Paid / Lost)
 *   job       — Job Flow
 *   shortlist — Shortlist Flow (a candidate's stage ON a shortlist: `shortlist_candidates.stage_id`)
 *   candidate — Candidate Flow (the candidate's own flow, not tied to a job; nothing moves
 *               through it yet)
 *
 * TS-1888 split the old `shortlist_candidate` type in two: shortlist places moved to the new
 * `shortlist` type, and `shortlist_candidate` was renamed `candidate`. 🔴 The rename is an EXPAND
 * step: this vocabulary says `candidate`, but `pipelines.applies_to` still STORES
 * `shortlist_candidate` (`toStoredPipelineType` / `fromStoredPipelineType` in `@crm/db`
 * translate), because the previous API, still serving while a deploy migrates, seeds a type's
 * default whenever it finds none. A later contract migration rewrites the stored value.
 *
 * Each type has one DEFAULT pipeline per organisation (where a new record starts); a type in
 * `MULTI_PIPELINE_TYPES` may have more (TS-1745). A stage's id never changes and neither does
 * its slug (taken from the name at creation): renaming only changes the label, so a record that
 * points at a stage, an integration keyed on the slug and the history that names it all stay
 * valid.
 */
export const PIPELINE_TYPES = ['project', 'deal', 'job', 'shortlist', 'candidate'];
export const pipelineTypeSchema = z.enum(PIPELINE_TYPES);
/**
 * The types an organisation may give more than one pipeline (TS-1745): every type. Projects,
 * candidates, deals and jobs hold places in several (`project_pipelines`, `candidate_pipelines`,
 * `deal_pipelines`, `job_pipelines`); a deal's or job's `stage_id` mirrors its place in the default.
 * A shortlisted candidate's stage still means the Shortlist Flow default. Kept as a list so a type can be narrowed back out without touching the routes.
 */
export const MULTI_PIPELINE_TYPES = PIPELINE_TYPES;
export const allowsSeveralPipelines = (type) => MULTI_PIPELINE_TYPES.includes(type);
/**
 * Each type renders in its own colour family, so a Deal Status chip can never be mistaken for
 * a Project Stage one; a stage's `colour` is a shade WITHIN its pipeline's family.
 */
export const COLOUR_FAMILIES = ['violet', 'emerald', 'sky', 'rose', 'amber'];
export const PIPELINE_TYPE_META = {
    project: { label: 'Sales Flow', family: 'violet' },
    deal: { label: 'Deal Flow', family: 'emerald' },
    job: { label: 'Job Flow', family: 'sky' },
    shortlist: { label: 'Shortlist Flow', family: 'rose' },
    candidate: { label: 'Candidate Flow', family: 'amber' },
};
export const STAGE_SHADES = ['200', '300', '400', '500', '600', '700'];
export const stageShadeSchema = z.enum(STAGE_SHADES);
/** A terminal stage ends the record's journey one way or the other. */
export const TERMINAL_KINDS = ['won', 'lost'];
export const terminalKindSchema = z.enum(TERMINAL_KINDS);
/** Lower-case words joined by hyphens: "Phase 1: Invited" → "phase-1-invited". */
export function slugifyStage(name) {
    const slug = name
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
        .replace(/-+$/g, '');
    return slug || 'stage';
}
const stageFields = {
    name: z.string().trim().min(1).max(80),
    colour: stageShadeSchema,
    isTerminal: z.boolean(),
    terminalKind: terminalKindSchema.nullable(),
    /** Likelihood (0–100) a record in this stage closes as won; null where it means nothing. */
    probability: z.number().int().min(0).max(100).nullable(),
};
/** A terminal stage says how it ends; an open one does not. */
function terminalRule(v, ctx) {
    if (v.isTerminal && !v.terminalKind)
        ctx.addIssue({
            code: 'custom',
            path: ['terminalKind'],
            message: 'a terminal stage needs terminalKind (won or lost)',
        });
    if (v.isTerminal === false && v.terminalKind)
        ctx.addIssue({
            code: 'custom',
            path: ['terminalKind'],
            message: 'only a terminal stage has a terminalKind',
        });
}
export const pipelineStageSchema = z.object({
    id: idSchema,
    pipelineId: idSchema,
    slug: z.string(),
    position: z.number().int(),
    ...stageFields,
    createdAt: instantSchema,
    updatedAt: instantSchema,
});
export const pipelineSchema = z.object({
    id: idSchema,
    appliesTo: pipelineTypeSchema,
    name: z.string(),
    /** The type's colour family, so a client renders every chip without its own table. */
    family: z.enum(COLOUR_FAMILIES),
    /** The type's default: where a new record starts. Exactly one per type; never archived. */
    isDefault: z.boolean(),
    /** Set while archived: hidden from pages and boards, its stages and history kept. */
    archivedAt: instantSchema.nullable(),
    stages: z.array(pipelineStageSchema),
    updatedAt: instantSchema,
});
const pipelineName = z.string().trim().min(1).max(80);
export const pipelinePatchSchema = z.object({ name: pipelineName });
export const pipelineListQuerySchema = z.object({
    /** `?includeArchived=true`: archived pipelines too (Settings shows them to unarchive). */
    includeArchived: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
});
/** A new stage is appended at the end; colour defaults to the family's middle shade. */
export const stageCreateSchema = z
    .object({
    name: stageFields.name,
    colour: stageFields.colour.default('500'),
    isTerminal: stageFields.isTerminal.default(false),
    terminalKind: stageFields.terminalKind.default(null),
    probability: stageFields.probability.default(null),
})
    .superRefine(terminalRule);
/**
 * A new pipeline (TS-1745), never the default. Without `stages` it starts from the type's
 * default stage list; with them, in the order given, and at least one must be open.
 */
export const pipelineCreateSchema = z.object({
    appliesTo: pipelineTypeSchema,
    name: pipelineName,
    stages: z
        .array(stageCreateSchema)
        .min(1)
        .max(30)
        .refine((stages) => stages.some((s) => !s.isTerminal), {
        message: 'a pipeline needs at least one open (non-terminal) stage',
    })
        .optional(),
});
/** Never `slug` or `position`: the slug is permanent and order changes through the reorder call. */
export const stagePatchSchema = z
    .object({
    name: stageFields.name,
    colour: stageFields.colour,
    isTerminal: stageFields.isTerminal,
    terminalKind: stageFields.terminalKind,
    probability: stageFields.probability,
})
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' });
/** The whole stage after a patch is applied must still pass this. */
export const stageStateSchema = z.object(stageFields).superRefine(terminalRule);
/** The pipeline's live stages, every one exactly once, in their new order. */
export const stageOrderSchema = z.object({
    stageIds: z
        .array(idSchema)
        .min(1)
        .refine((ids) => new Set(ids).size === ids.length, { message: 'a stage is listed twice' }),
});
const open = (name, colour, probability) => ({
    name,
    colour,
    ...(probability === undefined ? {} : { probability }),
});
const won = (name, probability) => ({
    name,
    colour: '700',
    terminalKind: 'won',
    ...(probability === undefined ? {} : { probability }),
});
const lost = (name, probability) => ({
    name,
    colour: '200',
    terminalKind: 'lost',
    ...(probability === undefined ? {} : { probability }),
});
/**
 * What an organisation starts with — seeded the first time it reads its pipelines, then
 * entirely the admins' to edit. Job Flow and Candidate Flow use the vocabulary the team works in
 * today in RecruitCRM (job stages the Relay task board tracks; the candidate hiring stages most
 * used over the last 90 days), so the move needs no relabelling. An organisation that already
 * has a type's default keeps it: changing a list here only reaches organisations (and pipelines
 * created without stages) from then on.
 */
export const DEFAULT_PIPELINES = {
    project: [
        open('Lead', '300', 10),
        open('Discovery', '400', 25),
        open('Proposal', '500', 50),
        open('Active', '600', 90),
        won('Won', 100),
        lost('Lost', 0),
    ],
    deal: [
        open('Proposal', '300', 25),
        open('Agreed', '400', 60),
        open('Invoiced', '500', 85),
        won('Paid', 100),
        lost('Lost', 0),
    ],
    job: [
        open('Hiring Form Submitted', '300'),
        open('Vetting Completed', '400'),
        open('Interview Invites Sent', '500'),
        open('On Hold', '600'),
        won('Filled'),
        lost('Closed'),
    ],
    // TODO(TS-1888): placeholder stages, so every organisation gets a Shortlist Flow its
    // shortlists can use. Replace them with the real Shortlist Flow stages once the team has
    // agreed them (organisations seeded meanwhile keep these until an admin edits them).
    shortlist: [
        open('Shortlisted', '300'),
        open('Submitted to Client', '400'),
        open('Client Interview', '500'),
        open('Offer', '600'),
        won('Hired'),
        lost('Not Selected'),
    ],
    candidate: [
        open('Phase 1: Invited to Interview', '300'),
        open('Phase 2: Interview Booked', '400'),
        open('Phase 3: Passed', '500'),
        won('Placed'),
        lost('Failed Interview'),
        lost('Disqualified'),
    ],
};
/**
 * Stage roll-up rules (behind `pipelines.rollup_rules`): "when ANY / ALL of a record's children
 * reach this stage, move the parent to that stage" — e.g. all of a job's shortlisted candidates
 * Hired → the job is Filled. A rule always points one level UP the model (shortlist place → job
 * → deal → project). This ticket stores and validates the rules; applying them arrives with the
 * records they move. Candidate Flow is the person's own flow, not a child of a job, so it rolls
 * up nowhere: a rule stored with a Candidate Flow source before TS-1888 still lists, but no new
 * one can be made.
 */
export const PIPELINE_PARENT = {
    project: null,
    deal: 'project',
    job: 'deal',
    shortlist: 'job',
    candidate: null,
};
export const ROLLUP_CONDITIONS = ['any', 'all'];
export const rollupConditionSchema = z.enum(ROLLUP_CONDITIONS);
export const rollupRuleInputSchema = z.object({
    sourceStageId: idSchema,
    condition: rollupConditionSchema,
    targetStageId: idSchema,
});
export const rollupRuleSchema = rollupRuleInputSchema.extend({
    id: idSchema,
    sourcePipeline: pipelineTypeSchema,
    targetPipeline: pipelineTypeSchema,
    createdAt: instantSchema,
});
//# sourceMappingURL=pipelines.js.map